import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { organization } from "../lib/auth-client.ts";
import { STORAGE_KEYS, slugify } from "@currencycore/shared";

/**
 * 1. Fetch the user's org list.
 * 2. Reuse the sticky localStorage org only if the current user still belongs to it.
 * 3. If the cache is stale, pick the first real org and replace the cache.
 * 4. If the user has no orgs, clear stale cache and show the create-workspace form.
 */
export function DashboardRedirect() {
  const navigate = useNavigate();
  const [noOrgs, setNoOrgs] = useState(false);
  const [creating, setCreating] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const result = await organization.list();
        const orgs = result.data ?? [];
        const cached = localStorage.getItem(STORAGE_KEYS.lastOrgId);

        if (orgs.length === 0) {
          localStorage.removeItem(STORAGE_KEYS.lastOrgId);
          setNoOrgs(true);
          return;
        }

        const cachedOrg = cached ? orgs.find((org) => org.id === cached) : null;
        const targetOrg = cachedOrg ?? orgs[0]!;
        localStorage.setItem(STORAGE_KEYS.lastOrgId, targetOrg.id);
        navigate(`/dashboard/${targetOrg.id}`, { replace: true });
      } catch {
        setNoOrgs(true);
      }
    })();
  }, [navigate]);

  async function handleCreateOrg(e: React.FormEvent) {
    e.preventDefault();
    if (!orgName.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const suffix = Math.random().toString(36).slice(2, 8);
      const result = await organization.create({
        name: orgName.trim(),
        slug: `${slugify(orgName)}-${suffix}`,
      });
      const newOrg = result.data;
      if (!newOrg) throw new Error("Failed to create organization.");
      localStorage.setItem(STORAGE_KEYS.lastOrgId, newOrg.id);
      navigate(`/dashboard/${newOrg.id}`, { replace: true });
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Failed to create organization.");
    } finally {
      setCreating(false);
    }
  }

  if (!noOrgs) {
    return (
      <div className="auth-page">
        <div className="loading-stack">
          <span className="spinner spinner-lg" />
          <p>Loading workspace…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-wrap animate-fade-in">
        <div className="auth-logo">
          <div className="auth-logo-mark">
            <img src="/logo.webp" alt="CurrencyCore" />
          </div>
          <h1>Create your workspace</h1>
          <p>You don't have an organization yet. Create one to get started.</p>
        </div>

        <div className="auth-card">
          {createError && <div className="error-banner">{createError}</div>}

          <form onSubmit={handleCreateOrg} className="form-stack">
            <div className="form-group">
              <label htmlFor="orgName" className="form-label">Organization name</label>
              <input
                id="orgName"
                type="text"
                required
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                className="form-input"
                placeholder="Acme Corp"
              />
            </div>
            <button type="submit" disabled={creating} className="btn btn-primary btn-block">
              {creating ? (
                <>
                  <span className="spinner" />
                  Creating…
                </>
              ) : (
                "Create workspace"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
